import { ZuiBaseElement } from '@zywave/zui-base';
import { ZuiDOMTokenList } from './dom-token-list.js';
import { html, nothing } from 'lit';
import { property, queryAssignedNodes } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-flyout-css.js';

/**
 * `zui-flyout` is a panel that slides out from the right edge of the screen to display additional content.
 *
 * @element zui-flyout
 *
 * @slot - Default, unnamed slot; for inserting content into `<zui-flyout>`.
 * @slot header - Slot for inserting content into the header of `<zui-flyout>`.
 * @slot footer - Slot for inserting content into the footer of `<zui-flyout>`.
 *
 * @cssprop [--zui-flyout-top] - (optional) Not set; Override top position for `<zui-flyout>` in inline mode. Unnecessary if using `<zui-flyout>` inside of `<zui-shell>` or `<zywave-shell>`.
 */

export class ZuiFlyoutElement extends ZuiBaseElement {
  static #flyouts: Set<ZuiFlyoutElement> = new Set<ZuiFlyoutElement>();

  #originalBodyOverflow: string | null = null;
  #mode: FlyoutMode = 'overlay';
  #opened = false;
  #expanded = false;
  #hasFooterContent = false;
  #controlsList = new ZuiDOMTokenList();
  #boundNavCollapseChange = this.#onNavCollapseChange.bind(this);
  #internals: ElementInternals;
  #identifier = Symbol();

  get #contentElement(): HTMLElement | null | undefined {
    if (this.contentSelector) {
      return document.querySelector(this.contentSelector);
    }
    const zuiShellElement = this._getElements('ZUI-SHELL')?.[0];
    if (zuiShellElement) {
      return zuiShellElement;
    }
    return document.querySelector('body');
  }

  static get styles() {
    return [super.styles, style];
  }

  /**
   * The mode of the flyout.
   * @type { 'overlay' | 'inline' }
   */
  @property({ type: String, reflect: true })
  get mode(): FlyoutMode {
    return this.#mode;
  }

  set mode(value: FlyoutMode) {
    const oldVal = this.#mode;
    if (oldVal !== value) {
      this.#mode = value;
      this.#onModeChanged(value, oldVal);
    }
  }

  /**
   * Returns a `ZuiDOMTokenList` containing the tokens of the `controlslist` attribute.
   *
   * When setting this property, provide a space-separated string of tokens. The tokens will be parsed
   * and added to the internal token list. Any tokens not present in the new value will be removed.
   *
   * **Supported tokens:**
   * - `noclose` - Hides the close button from the flyout header
   *
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/controlsList | HTMLMediaElement.controlsList}
   */
  get controlsList(): ZuiDOMTokenList {
    return this.#controlsList;
  }

  @property({
    attribute: 'controlslist',
    converter: {
      fromAttribute: (value: string | null) => value,
      toAttribute: (value: ZuiDOMTokenList) => {
        const tokens = [...value];
        return tokens.length > 0 ? tokens.join(' ') : null;
      },
    },
  })
  set controlsList(value: string | null) {
    if (!value) {
      return;
    }
    const tokens = value.split(/\s+/).filter((t) => t);
    for (const token of tokens) {
      this.#controlsList.add(token);
    }
    const tokensToRemove = [...this.#controlsList].filter((token) => !tokens.includes(token));
    for (const token of tokensToRemove) {
      this.#controlsList.remove(token);
    }
  }

  /**
   * Specifies a CSS selector that targets the main content element of the application, allowing `<zui-flyout mode="inline">` to push the content over when opened.
   * Used when {@link mode} is set to `inline` in order to properly push content over.
   * This element will receive a class of `zui-flyout-inline-opened` when the flyout is opened.
   * @see mode
   */
  @property({ type: String, attribute: 'content-selector' })
  contentSelector: string | null = null;

  /**
   * Represents if the dialog is currently open or not. Can apply to automatically open the dialog when attached to the DOM.
   */
  @property({ type: Boolean, reflect: true })
  get opened(): boolean {
    return this.#opened;
  }

  set opened(val: boolean) {
    const oldVal = this.#opened;
    if (oldVal !== val) {
      this.#opened = val;
      this.#onOpenedChanged(val, oldVal);
    }
  }

  /**
   * Represents if the flyout is currently expanded or not.
   */
  @property({ type: Boolean, reflect: true })
  get expanded(): boolean {
    return this.#expanded;
  }

  set expanded(val: boolean) {
    const oldVal = this.#expanded;
    if (oldVal !== val) {
      this.#expanded = val;
    }
  }

  get #dialog(): HTMLDialogElement | null {
    if (this.mode === 'overlay') {
      return this.shadowRoot?.querySelector('dialog') ?? null;
    }
    return null;
  }

  get #usingShell(): boolean {
    return !!this._getElements('ZUI-SHELL')?.length;
  }

  @queryAssignedNodes({ slot: 'footer' })
  private _footerSlottedNodes: Array<Node>;

  constructor() {
    super();

    this.#controlsList.addEventListener('tokenchange', () => this.requestUpdate());

    this.#internals = this.attachInternals();

    // initial a11y setup
    this.#internals.role = 'dialog';
    this.#internals.ariaModal = 'true';
    this.#internals.ariaHidden = 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('navcollapsechange', this.#boundNavCollapseChange);
    ZuiFlyoutElement.#flyouts.add(this);

    this.#ensureExternalDOMState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('navcollapsechange', this.#boundNavCollapseChange);
    ZuiFlyoutElement.#flyouts.delete(this);
    this.#ensureExternalDOMState();
  }

  /**
   * Opens the flyout.
   * @returns {Promise<void>} A promise that resolves when the flyout is opened.
   */
  async open(): Promise<void> {
    this.opened = true;

    await this.updateComplete;
  }

  /**
   * Closes the flyout.
   * @returns {Promise<void>} A promise that resolves when the flyout is closed.
   */
  async close(): Promise<void> {
    this.opened = false;
    this.expanded = false;

    await this.updateComplete;
  }

  /**
   * Expands the flyout
   */
  expand() {
    this.expanded = true;
  }

  /**
   * Collapses the flyout
   */
  collapse() {
    this.expanded = false;
  }

  render() {
    const contentRootStyles = {
      'using-shell': this.#usingShell,
    };
    return this.mode === 'inline'
      ? this.#renderInlineFlyout(contentRootStyles)
      : this.#renderOverlayFlyout(contentRootStyles);
  }

  #renderOverlayFlyout(contentRootClasses: { [key: string]: boolean }) {
    return html`<dialog class="content-root ${classMap(contentRootClasses)}" @close="${() => this.close()}">
      ${this.#renderInternals()}
    </dialog>`;
  }

  #renderInlineFlyout(contentRootClasses: { [key: string]: boolean }) {
    const zuiShell: HTMLElement = this._getElements('ZUI-SHELL')?.[0];
    const zuiShellNav: HTMLElement = this._getElements('ZUI-SHELL-NAV')?.[0];
    const classes = {
      ...contentRootClasses,
      'shell-has-app-name': !!zuiShell?.getAttribute('app-name'),
      'shell-nav-collapsed': !!zuiShellNav?.hasAttribute('collapsed'),
    };

    return html`<div class="content-root ${classMap(classes)}"> ${this.#renderInternals()} </div>`;
  }

  #renderInternals() {
    return html`
      <article>
        <header>
          ${this.#renderExpandCollapseButton()}
          <slot name="header"></slot>
          ${this.#renderCloseButton()}
        </header>
        <section>
          <slot></slot>
        </section>
        <footer class="${classMap({ hide: !this.#hasFooterContent })}">
          <slot name="footer" @slotchange="${this.#toggleFooter}"></slot>
        </footer>
      </article>
    `;
  }

  //todo: render if flyout is allowed to be expandable
  #renderExpandCollapseButton() {
    return this.expanded
      ? html`<button class="collapse" @click="${() => this.collapse()}">
          <zui-icon icon="zui-collapse" class="small"></zui-icon>
        </button>`
      : html`<button class="expand" @click="${() => this.expand()}">
          <zui-icon icon="zui-expand" class="small"></zui-icon>
        </button>`;
  }

  #renderCloseButton() {
    if (this.#controlsList.contains('noclose')) {
      return nothing;
    }
    return html`<button class="close" @click="${() => this.close()}">
      <zui-icon icon="zui-remove"></zui-icon>
    </button>`;
  }

  #onOpenedChanged(newVal: boolean, oldVal: boolean) {
    this.requestUpdate('opened', oldVal);
    if (newVal) {
      for (const flyout of ZuiFlyoutElement.#flyouts) {
        if (flyout.#identifier !== this.#identifier && flyout.opened) {
          flyout.close();
        }
      }
    }
    if (this.mode !== 'inline') {
      this.#ensureDialogState(newVal);
    }

    this.#ensureExternalDOMState();

    this.#internals.ariaHidden = newVal ? 'false' : 'true';
    const eventName = newVal ? 'open' : 'close';
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, composed: true }));
  }

  #onModeChanged(newVal: FlyoutMode, oldVal: FlyoutMode) {
    if (newVal === 'inline') {
      this.#internals.role = 'complementary';
      this.#internals.ariaModal = 'false';
    } else {
      this.#internals.role = 'dialog';
      this.#internals.ariaModal = 'true';
    }

    this.requestUpdate('mode', oldVal);
  }

  #onNavCollapseChange() {
    this.requestUpdate();
  }

  #toggleFooter() {
    this.#hasFooterContent = this._footerSlottedNodes.length > 0;
    this.requestUpdate();
  }

  /**
   * This method will ensure that the impacted layout changes are correct based on whether or not
   * any flyouts are open
   */
  #ensureExternalDOMState() {
    const flyouts = Array.from(ZuiFlyoutElement.#flyouts);
    const openedFlyouts = flyouts.filter((flyout) => flyout.opened && flyout.isConnected);
    const anyOpenInlineFlyouts = openedFlyouts.some((flyout) => flyout.mode === 'inline');
    const anyOpenedFlyouts = openedFlyouts.length > 0;

    // there is a potential bug where the body can change its overflow later, but we have an old reference
    // this could maybe be improved by some property on ZuiFlyoutElement for developers to provider and/or a MutationObserver
    if (this.#originalBodyOverflow === null) {
      this.#originalBodyOverflow = document.body.style.overflow;
    }

    document.body.style.overflow = anyOpenedFlyouts ? 'hidden' : this.#originalBodyOverflow;
    document.body.classList.toggle('zui-flyout-inline-opened', anyOpenInlineFlyouts);

    if (this.#contentElement) {
      this.#contentElement.classList.toggle('zui-flyout-inline-opened', anyOpenInlineFlyouts);
    }
    const zuiShellContentActionbar: HTMLElement = this._getElements('ZUI-SHELL-CONTENT-ACTIONBAR')?.[0];
    zuiShellContentActionbar?.classList.toggle('zui-flyout-inline-opened', anyOpenInlineFlyouts);
  }

  async #ensureDialogState(opened?: boolean) {
    if (!this.#dialog) {
      await this.updateComplete;
    }

    opened ??= this.opened;

    opened ? this.#dialog?.showModal() : this.#dialog?.close();
  }
}

window.customElements.define('zui-flyout', ZuiFlyoutElement);

type FlyoutMode = 'overlay' | 'inline';

declare global {
  interface HTMLElementTagNameMap {
    'zui-flyout': ZuiFlyoutElement;
  }
}
